import React, { useEffect, useRef, useState } from 'react';
import { CornerSeat } from '@ofself/shell';
import ActionsTable from './ActionsTable';
import Signals from './Signals';

// MISSION DETAIL — the page shell, PLAN and BOARD views, ported from
// legacy/app.js (2026-08-23, phase 4 of the React port). Portals into
// #page-mission; the .mission-main / .rm-* / .pl-* / .flow-* / .board-* dress
// in legacy/style.css applies unchanged.
//
// TWO VIEWS STAY THE ENGINE'S FOR NOW — the outliner and signals, the app's
// deepest interactive surfaces. They paint into the LEAF divs this page
// renders (#ol-mission / #signals-mission) via window.renderOutliner() /
// window.renderSignals(), reading the engine's _mission — which openMission
// still owns and announces here as missions:mission. Mutations round-trip
// through window.refreshMissionDetail() so both worlds repaint from one
// fetch.

// The board's columns, and the ONLY place the set is written down — the buckets,
// the totals and the CSS column count are all derived from this array, so adding
// a status here is the whole edit.
//
// `paused` had no column, and bucketize drops an action whose status matches
// none: a paused action was absent from the board altogether while still being
// counted in the section's total, so the tally read n-of-more-than-you-can-see.
// It sits after the in-flight states and before the terminal one, because it is
// work that started and stopped rather than work that finished.
//
// `not_needed` is still deliberately uncolumned — it means the work stopped
// being necessary, which is the one status that has no place on a board of what
// is being done. It remains the sole status this view drops.
const STATUS_COLS = [
  { key: 'todo', label: 'To do', statusValue: 'open', match: (s) => s === 'open' || !s },
  { key: 'in_progress', label: 'In progress', statusValue: 'in_progress', match: (s) => s === 'in_progress' },
  { key: 'reviewing', label: 'Reviewing', statusValue: 'reviewing', match: (s) => s === 'reviewing' },
  { key: 'paused', label: 'Paused', statusValue: 'paused', match: (s) => s === 'paused' },
  { key: 'done', label: 'Done', statusValue: 'completed', match: (s) => s === 'completed' },
];
// A section collapses when it holds nothing live — every column but the terminal
// one empty. Spelled against STATUS_COLS rather than naming todo/in_progress, so
// a new column counts as live without this line having to be remembered.
const LIVE_COLS = STATUS_COLS.filter((c) => c.key !== 'done');
const emptyBuckets = () => Object.fromEntries(STATUS_COLS.map((c) => [c.key, []]));
const CELL_CAP = 6;

function Avatars({ node }) {
  const html = window.assigneeAvatarsHtml?.(node) || '';
  return html ? <span dangerouslySetInnerHTML={{ __html: html }} /> : null;
}
function TagChips({ node }) {
  const tags = window.tagsForNode?.(node) || [];
  if (!tags.length) return null;
  return (
    <span className="pl-tags">
      {tags.map((t) => (
        <span key={t.id} className="tag-chip" data-color={t.color || 'sand'}>
          <span className="tag-chip-label">{t.label}</span>
        </span>
      ))}
    </span>
  );
}

// A dependency line, which is a door: it names a count and a date, and pressing
// it opens the node those refer to.
function DepLine({ line, clash }) {
  return (
    <div className={`pl-dep${clash ? ' is-clash' : ''}`} title={line.names}
         role="button" tabIndex={0}
         onClick={(e) => { e.stopPropagation(); window.openSheet?.(line.target.id); }}
         onKeyDown={(e) => {
           if (e.key !== 'Enter' && e.key !== ' ') return;
           e.preventDefault(); e.stopPropagation();
           window.openSheet?.(line.target.id);
         }}>
      {line.text}
    </div>
  );
}

// ── PLAN — goals against the dates they're aimed at (legacy renderPlanView) ──
function planMonthOf(d) { return d.getFullYear() * 12 + d.getMonth(); }
const PLAN_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const PLAN_UNITS = [1, 3, 6, 12];
const PLAN_MAX_COLS = 6;
function planUnitIndex(monthIdx, k) { return Math.floor(monthIdx / k); }
function planUnitLabel(unitIdx, k) {
  const m = unitIdx * k, y = Math.floor(m / 12), i = m % 12;
  if (k === 1) return `${PLAN_MONTHS[i]} ${y}`;
  if (k === 3) return `Q${Math.floor(i / 3) + 1} ${y}`;
  if (k === 6) return `H${Math.floor(i / 6) + 1} ${y}`;
  return String(y);
}
// The goal's OWN line says the day it's aimed at. The roadmap's column heads
// are buckets (month/quarter/half/year) because the chart has to fit a span
// into six columns — but a row carries a single date, and rounding that to the
// month threw away the one thing the goal actually committed to. Falls back to
// nothing when the goal has no date; the month label is still the fallback at
// the call site for a date we can't parse into a day.
function planDayLabel(node) {
  const d = node.target_date ? (window.parseYmdLocal?.(node.target_date) || new Date(node.target_date)) : null;
  if (!d || isNaN(d)) return '';
  return `${PLAN_MONTHS[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`;
}
// The day a goal is aimed at, as a sort key. Undated goals sort LAST: they have
// no place on a timeline, and floating them to the top buries the dated work
// this view exists to show. Infinity === Infinity, so two undated goals compare
// equal and keep their tree order rather than returning NaN from the subtraction.
function planSortKey(node) {
  const d = node.target_date ? (window.parseYmdLocal?.(node.target_date) || new Date(node.target_date)) : null;
  return (d && !isNaN(d)) ? d.getTime() : Infinity;
}
function planSpanOf(node) {
  const end = node.target_date ? (window.parseYmdLocal?.(node.target_date) || new Date(node.target_date)) : null;
  if (!end || isNaN(end)) return null;
  const created = node.created_at ? new Date(node.created_at) : null;
  const startD = (created && !isNaN(created) && created < end) ? created : end;
  return { s: planMonthOf(startD), e: planMonthOf(end) };
}
// ── Dependencies, rolled up to the goal rows this view draws ─────────────────
// An edge can hang off an ACTION deep inside a goal ("the Stripe integration
// task waits on the account approval"), and a goal is what this view draws — so
// an edge is attributed to whichever goal's subtree owns its near end, and to
// every goal above it, since a blocked child is a blocked parent.
//
// AN EDGE WHOLLY INSIDE ONE SUBTREE IS DROPPED for that goal. Two of its own
// actions ordered against each other say nothing about the goal's place in the
// plan; counted, every goal with a sequenced checklist inside it would report
// itself blocked by itself.
//
// A settled far end is not a dependency any more: a completed blocker blocks
// nothing, and work that was dropped is not waiting on you.
const SETTLED = new Set(['completed', 'not_needed']);
function subtreeIds(node) {
  const ids = new Set([node.id]);
  (function walk(n) { for (const c of (n.children || [])) { ids.add(c.id); walk(c); } })(node);
  return ids;
}
function planDependenciesFor(node, deps) {
  const out = { blockedBy: [], blocking: [] };
  if (!deps?.edges?.length) return out;
  const mine = subtreeIds(node);
  const seenB = new Set(), seenD = new Set();
  for (const e of deps.edges) {
    const fromMine = mine.has(e.from_node_id);
    const toMine = mine.has(e.to_node_id);
    if (fromMine === toMine) continue;               // inside, or nothing to do with this goal
    const far = deps.nodes[fromMine ? e.to_node_id : e.from_node_id];
    if (!far || SETTLED.has(far.status)) continue;
    // from -> to reads "from depends on to": the far end of an outgoing edge is
    // what this goal waits on, the far end of an incoming one is what waits on it.
    const [bucket, seen] = fromMine ? [out.blockedBy, seenB] : [out.blocking, seenD];
    if (seen.has(far.id)) continue;                  // several actions, one blocker
    seen.add(far.id);
    bucket.push(far);
  }
  return out;
}
// HOW MANY and WHEN, not who. A goal's title is routinely a whole sentence
// ("SDK API speeds are sufficiently performant, with expected SLAs &
// monitoring"), so naming the far end turned a one-line row into a paragraph —
// and named the same thing twice when the dates disagreed. The count and the
// date are what change a decision; the names ride on the hover title, and the
// node's own detail sheet has them in full.
//
// WHICH date matters differs by direction. What blocks you is bounded by the
// LAST blocker to land — you cannot start until all of them have. What waits on
// you is pressed by the FIRST one due: that is the one you are already making
// late.
function planDepLine(list, { verb, verbPlural, pick }) {
  if (!list.length) return null;
  const kinds = new Set(list.map((n) => n.kind || 'item'));
  const word = kinds.size === 1 ? [...kinds][0] : 'item';
  const dated = list.filter((n) => planSortKey(n) !== Infinity);
  const chosen = dated.length
    ? dated.reduce((a, b) => {
        const beats = pick === 'latest' ? planSortKey(b) > planSortKey(a) : planSortKey(b) < planSortKey(a);
        return beats ? b : a;
      })
    : null;
  return {
    text: `${list.length} ${word}${list.length === 1 ? '' : 's'} ${list.length === 1 ? verb : verbPlural} this`
      + (chosen ? ` (dated ${planDayLabel(chosen)})` : ''),
    key: chosen ? planSortKey(chosen) : Infinity,
    names: list.map((n) => n.title).join(', '),
    // WHERE THE LINE GOES when pressed. The one the date names — the blocker
    // that actually bounds you, or the dependent you are soonest making late —
    // since that is the one the row is making a claim about. With no dates at
    // all there is nothing to prefer, so the first stands in.
    target: chosen || list[0],
  };
}
function planSubtreeActions(node) {
  const out = [];
  (function walk(n) {
    for (const c of (n.children || [])) {
      if (c.kind === 'action') out.push(c);
      walk(c);
    }
  })(node);
  return out;
}

// The draft is a LINE YOU ARE TYPING ON, not a row that already exists.
function GoalDraft({ parentId, onDone, onCancel }) {
  const ref = useRef(null);
  const submitting = useRef(false);
  useEffect(() => {
    const input = ref.current;
    input.focus();
    const range = document.createRange();
    range.selectNodeContents(input);
    range.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);
  const commit = async () => {
    if (submitting.current) return;
    const title = (ref.current?.textContent || '').trim();
    if (!title) { onCancel(); return; }
    submitting.current = true;
    try {
      await window.postJson(`/api/items/${parentId}/children`, { kind: 'goal', title });
      window.invalidateMissionCache?.();
      onDone();
    } catch (err) {
      alert(`Create failed: ${err.message}`);
      submitting.current = false;
    }
  };
  return (
    <div className="pl-draft">
      <div ref={ref} className="pl-draft-input" contentEditable data-placeholder="name the goal…" spellCheck={false}
           onKeyDown={(e) => {
             if (e.key === 'Enter') { e.preventDefault(); commit(); }
             if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
           }}
           onBlur={() => setTimeout(() => {
             if (submitting.current) return;
             if (!(ref.current?.textContent || '').trim()) onCancel(); else commit();
           }, 120)} />
    </div>
  );
}

function PlanView({ mission }) {
  const [drafting, setDrafting] = useState(false);
  // CHRONOLOGICAL, not the outline's order. The tree arrives in the order the
  // outliner stores — the one the user dragged — and this view inherited it, so
  // a list whose whole right-hand column is dates read Oct 15, Nov 1, Oct 5.
  // Sorted per SIBLING SET rather than over a flattened list: a sub-goal sorted
  // globally would drift away from the parent it belongs to, and the indent
  // would then be describing a nesting the order contradicts. A mission with no
  // sub-goals — the common shape, and the one in the screenshot — is a single
  // sibling set, so it reads as plain date order.
  const goals = [];
  (function walk(nodes, depth) {
    const inDateOrder = [...(nodes || [])].sort((a, b) => {
      const ka = planSortKey(a), kb = planSortKey(b);
      return ka === kb ? 0 : ka - kb;    // stable, so same-day goals keep tree order
    });
    for (const n of inDateOrder) {
      if (n.kind === 'goal') { goals.push({ node: n, depth }); walk(n.children || [], depth + 1); }
      else walk(n.children || [], depth);
    }
  })(mission.items || [], 0);

  const rows = goals.map((g) => {
    const acts = planSubtreeActions(g.node);
    const done = acts.filter((a) => a.status === 'completed').length;
    const moving = acts.filter((a) => a.status === 'in_progress').length;
    return { ...g, acts, done, moving, sp: planSpanOf(g.node), dep: planDependenciesFor(g.node, mission.dependencies) };
  });
  const dated = rows.filter((r) => r.sp);

  let chart = null;
  if (dated.length) {
    const nowM = planMonthOf(new Date());
    let firstM = Math.max(nowM, Math.min(...dated.map((r) => r.sp.s)));
    const lastM = Math.max(...dated.map((r) => r.sp.e));
    if (firstM > lastM) firstM = lastM;
    const k = PLAN_UNITS.find((u) => planUnitIndex(lastM, u) - planUnitIndex(firstM, u) + 1 <= PLAN_MAX_COLS)
      || PLAN_UNITS[PLAN_UNITS.length - 1];
    const firstU = planUnitIndex(firstM, k);
    const lastU = planUnitIndex(lastM, k);
    const nowU = planUnitIndex(nowM, k);
    const cols = [];
    for (let i = firstU; i <= lastU; i++) cols.push(i);
    const tone = (endU) => (endU <= nowU ? 'near' : endU <= nowU + 1 ? 'next' : 'later');
    const undated = goals.length - dated.length;
    chart = (
      <>
        <div className="plan-sec">
          <span className="t">Roadmap</span>
          {undated ? <span className="m">{undated} without a date</span> : null}
        </div>
        <div className="rm" style={{ gridTemplateColumns: `minmax(140px, 220px) repeat(${cols.length},minmax(0,1fr))` }}>
          <div className="rm-head">Goal</div>
          {cols.map((c) => (
            <div key={c} className={`rm-head rm-q${c === nowU ? ' is-now' : ''}`}>{planUnitLabel(c, k)}</div>
          ))}
          {rows.map((r) => {
            const meta = r.acts.length
              ? `${r.acts.length} action${r.acts.length === 1 ? '' : 's'}${r.moving ? ` · ${r.moving} in progress` : ''}`
              : '';
            let bar = null;
            if (r.sp) {
              const a = Math.max(planUnitIndex(r.sp.s, k), firstU);
              const b = Math.min(planUnitIndex(r.sp.e, k), lastU);
              if (b >= a) {
                bar = <div className={`rm-bar ${tone(planUnitIndex(r.sp.e, k))}`}
                           style={{ gridColumn: `${a - firstU + 1}/${b - firstU + 2}` }} />;
              }
            }
            return (
              <div key={r.node.id} className="rm-row" onClick={() => window.openSheet?.(r.node.id)}>
                <div className="rm-goal" style={{ paddingLeft: 16 + r.depth * 12 }}>
                  <div className="rm-name">{r.node.title || 'untitled'}</div>
                  {meta && <div className="rm-meta">{meta}</div>}
                </div>
                <div className="rm-track" style={{ gridColumn: '2/-1', gridTemplateColumns: `repeat(${cols.length},minmax(0,1fr))` }}>
                  <div className="rm-rules" style={{ gridTemplateColumns: `repeat(${cols.length},minmax(0,1fr))` }}>
                    {cols.map((c) => <div key={c} />)}
                  </div>
                  {bar}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <>
      {chart}
      <div className={`plan-sec${chart ? ' spaced' : ''}`}>
        <span className="m">what has to become true</span>
      </div>
      <div className="pl">
        {rows.map((r) => {
          const pct = r.acts.length ? Math.round((r.done / r.acts.length) * 100) : 0;
          // Just the count. The progress clause that used to trail this ("· 3 in
          // progress") is the same fact as the bar and the "3 / 12 done" tally
          // sitting two columns to the right.
          const support = r.acts.length
            ? `supported by ${r.acts.length} action${r.acts.length === 1 ? '' : 's'}`
            : '';
          // Both directions, because they answer different questions: what is
          // stopping this, and what does finishing it release.
          const blocked = planDepLine(r.dep.blockedBy, { verb: 'blocks', verbPlural: 'block', pick: 'latest' });
          const waits = planDepLine(r.dep.blocking, { verb: 'waits on', verbPlural: 'wait on', pick: 'earliest' });
          // THE CONTRADICTION — a goal dated before the goal it waits on is a
          // plan that cannot happen, and this view is the only one holding both
          // the dates and the edges. It used to say so in a second line, which
          // repeated the blocker's title and its date to make a point the reader
          // can see the moment both dates are on the row. So the line that
          // already states them carries it in its colour: same words, and the
          // impossible case is the only red thing on the page.
          const clash = !!blocked && blocked.key !== Infinity
            && planSortKey(r.node) !== Infinity && blocked.key > planSortKey(r.node);
          const phase = planDayLabel(r.node) || (r.sp ? planUnitLabel(planUnitIndex(r.sp.e, 1), 1) : '');
          return (
            <div key={r.node.id} className="pl-row" onClick={() => window.openSheet?.(r.node.id)}>
              <div className="pl-main" style={{ paddingLeft: r.depth * 14 }}>
                <div className="pl-title">
                  <span className="pl-title-text">{r.node.title || 'untitled'}</span>
                  <TagChips node={r.node} />
                </div>
                {support && <div className="pl-support">{support}</div>}
                {/* Pressable, and it has to be: the row dropped the names to stay one
                    line, so the way to find out WHICH goal blocks this is to go there.
                    stopPropagation because the row itself opens this goal — without it
                    the click would open the thing you were trying to leave. The names
                    stay on the hover for the answer that doesn't need a trip. */}
                {blocked && <DepLine line={blocked} clash={clash} />}
                {waits && <DepLine line={waits} />}
              </div>
              {/* THE SIDE IS ONE CLUSTER (2026-09-22). Four fixed grid columns
                  (1fr 190px 150px 90px) were 502px of furniture; beside the
                  agent the content column is ~430px, and the title column
                  collapsed to one letter per line. The row is title + a side
                  now, and the side takes what it needs. */}
              <div className="pl-side">
                {r.acts.length ? (
                  <div className="pl-prog">
                    <div className="pl-bar"><i style={{ width: `${pct}%` }} /></div>
                    <div className="pl-count">{r.done} / {r.acts.length}</div>
                  </div>
                ) : null}
                {phase ? <div className="pl-phase">{phase}</div> : null}
                <div className="pl-owner"><Avatars node={r.node} /></div>
              </div>
            </div>
          );
        })}
      </div>
      {drafting
        ? <GoalDraft parentId={mission.id}
                     onDone={() => { setDrafting(false); window.refreshMissionDetail?.(); }}
                     onCancel={() => setDrafting(false)} />
        : null}
      <div className="addline" onClick={(e) => { e.stopPropagation(); setDrafting(true); }}>+ add a goal</div>
    </>
  );
}

// ── BOARD — sections per top-level goal, three status columns (legacy) ───────
function BoardCard({ a, dragState }) {
  const done = a.status === 'completed';
  return (
    <div className={`board-card${done ? ' done' : ''}`} draggable
         onClick={() => window.openSheet?.(a.id)}
         onDragStart={(e) => {
           dragState.current = { id: a.id, status: a.status || 'open' };
           e.dataTransfer.effectAllowed = 'move';
           e.dataTransfer.setData('text/plain', a.id);
           e.currentTarget.classList.add('dragging');
           /* An empty column gives up its outline at rest, so the board must
              show its shape again while something is looking for somewhere to
              land — otherwise you would be dragging at invisible targets. */
           document.body.classList.add('board-dragging');
         }}
         onDragEnd={(e) => {
           e.currentTarget.classList.remove('dragging');
           dragState.current = null;
           document.body.classList.remove('board-dragging');
           document.querySelectorAll('.flow-cell.drag-over').forEach((c) => c.classList.remove('drag-over'));
         }}>
      <div className="title">{a.title || 'untitled'}</div>
      {(a._via || window.assigneeAvatarsHtml?.(a)) ? (
        <div className="meta">
          {a._via && <span className="via">{a._via}</span>}
          <Avatars node={a} />
        </div>
      ) : null}
    </div>
  );
}

function BoardCell({ actions, statusValue, dragState, onDropStatus }) {
  const [expanded, setExpanded] = useState(false);
  const visible = actions.slice(0, CELL_CAP);
  const overflow = actions.slice(CELL_CAP);
  return (
    // data-empty so an empty column can stand down to its floor — a tray only
    // draws its outline when there is something in it, or when a drag is
    // looking for somewhere to land (.drag-over).
    <div className={`flow-cell${expanded ? ' expanded' : ''}`} data-status={statusValue}
         data-empty={actions.length === 0 || undefined}
         onDragOver={(e) => {
           if (!dragState.current) return;
           e.preventDefault();
           e.dataTransfer.dropEffect = 'move';
           document.querySelectorAll('.flow-cell.drag-over').forEach((c) => c.classList.remove('drag-over'));
           e.currentTarget.classList.add('drag-over');
         }}
         onDragLeave={(e) => {
           if (!e.currentTarget.contains(e.relatedTarget)) e.currentTarget.classList.remove('drag-over');
         }}
         onDrop={async (e) => {
           e.preventDefault();
           e.currentTarget.classList.remove('drag-over');
           const drag = dragState.current;
           if (!drag || drag.status === statusValue) return;
           onDropStatus(drag.id, statusValue);
         }}>
      {visible.map((a) => <BoardCard key={a.id} a={a} dragState={dragState} />)}
      {overflow.length ? (
        <>
          {!expanded && (
            <div className="more-link" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
              + {overflow.length} more
            </div>
          )}
          <div className="hidden-overflow">
            {overflow.map((a) => <BoardCard key={a.id} a={a} dragState={dragState} />)}
          </div>
        </>
      ) : null}
    </div>
  );
}

function BoardView({ mission }) {
  const dragState = useRef(null);
  const [openWrapped, setOpenWrapped] = useState(() => new Set());

  function collectActions(topGoal) {
    const out = [];
    (function walk(n, immediateParent) {
      if (n.kind === 'action') out.push({ ...n, _immediateParent: immediateParent });
      for (const c of (n.children || [])) walk(c, n);
    })(topGoal, null);
    // walk starts at the goal itself; drop the goal node walk quirk by
    // walking its children with the goal as parent (the legacy shape)
    return out
      .filter((a) => a.id !== topGoal.id)
      .map((a) => ({
        ...a,
        _via: a._immediateParent && a._immediateParent.id !== topGoal.id ? a._immediateParent.title : null,
      }));
  }
  const bucketize = (actions) => {
    const buckets = emptyBuckets();
    for (const a of actions) {
      const col = STATUS_COLS.find((c) => c.match(a.status));
      if (col) buckets[col.key].push(a);
    }
    return buckets;
  };
  const totals = Object.fromEntries(STATUS_COLS.map((c) => [c.key, 0]));
  const addTotals = (buckets) => {
    for (const c of STATUS_COLS) totals[c.key] += buckets[c.key].length;
  };
  const sections = [];
  let directSection = null;
  for (const it of (mission.items || [])) {
    if (it.kind === 'goal') {
      const actions = collectActions(it);
      if (!actions.length) continue;
      const buckets = bucketize(actions);
      addTotals(buckets);
      const wrapped = LIVE_COLS.every((c) => buckets[c.key].length === 0);
      sections.push({ goal: it, buckets, wrapped, totalCount: actions.length });
    } else if (it.kind === 'action') {
      if (!directSection) {
        directSection = {
          goal: { id: mission.id, title: '' },
          buckets: emptyBuckets(),
          wrapped: false,
          totalCount: 0,
        };
        sections.push(directSection);
      }
      const col = STATUS_COLS.find((c) => c.match(it.status));
      if (col) directSection.buckets[col.key].push(it);
      directSection.totalCount++;
    }
  }
  if (directSection) addTotals(directSection.buckets);

  const onDropStatus = async (id, newStatus) => {
    try {
      await window.putJson(`/api/items/${id}`, { value_json: { status: newStatus } });
      window.refreshMissionDetail?.();
    } catch (err) {
      alert('Move failed: ' + err.message);
    }
  };

  return (
    <>
      <div className="flow-cols-head">
        {STATUS_COLS.map((c) => (
          <div key={c.key} className="flow-col-head-cell"><span>{c.label}</span><span className="count">{totals[c.key]}</span></div>
        ))}
      </div>
      {sections.length ? sections.map((s) => {
        const named = !!(s.goal.title || '').trim();
        const total = s.totalCount ?? STATUS_COLS.reduce((n, c) => n + s.buckets[c.key].length, 0);
        const open = openWrapped.has(s.goal.id);
        return (
          <section key={s.goal.id}
                   className={`flow-section${s.wrapped ? ' wrapped' : ''}${open ? ' open' : ''}${named ? '' : ' unnamed'}`}
                   onClick={(e) => {
                     if (!s.wrapped) return;
                     if (e.target.closest('.board-card')) return;
                     setOpenWrapped((cur) => {
                       const next = new Set(cur);
                       if (next.has(s.goal.id)) next.delete(s.goal.id); else next.add(s.goal.id);
                       return next;
                     });
                   }}>
            {named && (
              <div className="flow-section-head">
                <h3 className="flow-section-name">{s.wrapped ? <span className="flow-section-toggle">▸</span> : null}{s.goal.title}</h3>
                <span className="flow-section-rule" />
                <span className="flow-section-tally">{s.buckets.done.length} / {total}</span>
              </div>
            )}
            <div className="flow-cols">
              {STATUS_COLS.map((c) => (
                <BoardCell key={c.key} actions={s.buckets[c.key]} statusValue={c.statusValue}
                           dragState={dragState} onDropStatus={onDropStatus} />
              ))}
            </div>
          </section>
        );
      }) : <div className="board-empty">no sub-goals yet — add one in the outline</div>}
    </>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────
function StatementBlock({ mission }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef(null);
  const grow = (el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };
  useEffect(() => { if (editing) { const el = ref.current; if (el) { grow(el); el.focus(); el.setSelectionRange(el.value.length, el.value.length); } } }, [editing]);
  const start = () => { setDraft(mission.statement || ''); setEditing(true); };
  const commit = async () => {
    setEditing(false);
    const next = draft.trim();
    if (next === (mission.statement || '')) return;
    try {
      await window.putJson(`/api/items/${mission.id}`, { value_json: { description: next || null } });
      await window.afterNodeWrite?.(mission.id, { description: next || null });
    } catch (err) { alert(`Update failed: ${err.message}`); }
  };
  if (editing) {
    return (
      <textarea
        ref={ref}
        className={`text-prose lane-founding--block is-editing${draft ? '' : ' is-empty'}`}
        rows={1}
        value={draft}
        placeholder="what this mission is for"
        onChange={(e) => { setDraft(e.target.value); grow(e.target); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setDraft(mission.statement || ''); setEditing(false); }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); e.target.blur(); }
        }}
      />
    );
  }
  return (
    <button type="button" className={`text-prose lane-founding--block${mission.statement ? '' : ' is-empty'}`} onClick={start} title="press to edit">
      {mission.statement || 'what this mission is for'}
    </button>
  );
}

export default function Mission({ active, mission, view }) {
  if (view === 'board') view = 'outline'; // the board folded into actions (2026-09-10)

  // Not standing → render nothing, so the builder page's own #ol-mission is
  // the only one in the document (the outliner finds by id).
  if (!active) return null;
  if (!mission) return <div className="loading">loading…</div>;
  if (mission.error) return <div className="empty-state">Couldn't load mission: {mission.error}</div>;

  return (
    <div className="mission-main">
      {/* THE DETAILS DOOR IS A SEAT IN THE KIT'S TOP-RIGHT ARRAY (owner report
          2026-09-22: it collided with FOCUS). Positioned by hand it landed on
          the frame's own seat; as a <CornerSeat> the kit lines it up left of
          FOCUS. A word, not a glyph — it says what it opens. */}
      <CornerSeat>
        <button type="button" className="ofself-nav-toggle" aria-label="Mission details"
                onClick={() => window.openSheet?.(mission.id)}>
          <span className="ofself-seat-word">Details</span>
        </button>
      </CornerSeat>
      {/* THE STATEMENT IS A BLOCK, AND IT EDITS (owner call 2026-09-10): the
          mission's sentence stands as the first block of the grid the table
          continues — full-bleed, seamed — and a press on it opens it for
          editing in place. Saved as the mission's description (the field the
          statement has always read from). */}
      <StatementBlock mission={mission} />
      {view === 'plan' && <div className="plan-view"><PlanView mission={mission} /></div>}
      {view === 'outline' && (
        /* THE DATABASE (owner call 2026-09-10): the actions as a sheet of
           properties, edited in place — the kit's <DataTable>. Its header row
           names the surface, so the "what needs to be done" head that stood
           over the old bare list retired with it. */
        <div className="outliner outliner--table">
          <ActionsTable
            mission={mission}
            /* THE BOARD IS A VIEW OF THE ACTIONS (owner call 2026-09-10, "fold
               the board view into it, just like Notion"): the same columns
               the old Board page drew, under the table's views strip. */
            views={{
              board: (
                <div className="board-flow" style={{ '--flow-cols': STATUS_COLS.length }}>
                  <BoardView mission={mission} />
                </div>
              ),
            }}
          />
        </div>
      )}
      {/* The column count travels with the columns: the two grids in the sheet
          read --flow-cols, so adding a status to STATUS_COLS cannot leave the
          CSS still saying three. */}
      {view === 'signals' && <div className="signals-view"><Signals mission={mission} /></div>}
    </div>
  );
}
